import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabase';

interface StripeTransaction {
  id: string;
  stripe_transaction_id: string;
  amount: number;
  currency: string;
  status: string;
  transaction_type: string;
  description: string;
  customer_email: string | null;
  customer_name: string | null;
  created_at: string;
  metadata: Record<string, any>;
  is_manual: boolean;
}

interface StripeSubscription {
  id: string;
  stripe_subscription_id: string;
  customer_id: string | null;
  customer_email: string | null;
  customer_name: string | null;
  status: string;
  current_period_start: string;
  current_period_end: string;
  amount: number;
  currency: string;
  interval: string;
  cancel_at_period_end: boolean;
  metadata: Record<string, any>;
  synced_at: string;
  created_at: string;
  updated_at: string;
}

interface ExpenseData {
  id: string;
  amount: number;
  month: number;
  year: number;
  notes: string | null;
  cash_on_hand: number;
  updated_at: string;
}

interface StripeConfig {
  last_synced_at: string | null;
  stripe_publishable_key: string | null;
}

interface FinancialMetrics {
  mtdRevenue: number;
  projectedRevenue: number;
  previousMonthRevenue: number;
  growthRate: number;
  runRate: number;
  profitRunRate: number;
  currentMonthExpenses: number;
  cashRunway: number;
}

interface StripeFinancialsContextType {
  transactions: StripeTransaction[];
  expenses: ExpenseData | null;
  metrics: FinancialMetrics;
  stripeConfig: StripeConfig | null;
  isLoading: boolean;
  error: string | null;
  refreshTransactions: () => Promise<void>;
  syncStripeData: () => Promise<void>;
  updateExpenses: (amount: number, notes: string, cashOnHand?: number) => Promise<void>;
}

const StripeFinancialsContext = createContext<StripeFinancialsContextType | null>(null);

export function StripeFinancialsProvider({ children }: { children: ReactNode }) {
  const [transactions, setTransactions] = useState<StripeTransaction[]>([]);
  const [subscriptions, setSubscriptions] = useState<StripeSubscription[]>([]);
  const [expenses, setExpenses] = useState<ExpenseData | null>(null);
  const [allExpenses, setAllExpenses] = useState<ExpenseData[]>([]);
  const [stripeConfig, setStripeConfig] = useState<StripeConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();

  const calculateMetrics = (): FinancialMetrics => {
    const now = new Date();
    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const previousMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const previousMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
    const yearStart = new Date(now.getFullYear(), 0, 1);

    const mtdTransactions = transactions.filter((t) => {
      const transactionDate = new Date(t.created_at);
      return transactionDate >= currentMonthStart && t.amount > 0;
    });

    const previousMonthTransactions = transactions.filter((t) => {
      const transactionDate = new Date(t.created_at);
      return (
        transactionDate >= previousMonthStart &&
        transactionDate <= previousMonthEnd &&
        t.amount > 0
      );
    });

    const ytdTransactions = transactions.filter((t) => {
      const transactionDate = new Date(t.created_at);
      return transactionDate >= yearStart && t.amount > 0;
    });

    const mtdRevenue = mtdTransactions.reduce((sum, t) => sum + t.amount / 100, 0);
    const previousMonthRevenue = previousMonthTransactions.reduce(
      (sum, t) => sum + t.amount / 100,
      0
    );
    const ytdRevenue = ytdTransactions.reduce((sum, t) => sum + t.amount / 100, 0);

    const growthRate =
      previousMonthRevenue > 0
        ? ((mtdRevenue - previousMonthRevenue) / previousMonthRevenue) * 100
        : 0;

    const runRate = mtdRevenue * 12;

    // Calculate projected revenue: MTD + remaining subscription renewals for this month
    const currentMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
    const upcomingSubscriptionRevenue = subscriptions
      .filter((sub) => {
        const periodEnd = new Date(sub.current_period_end);
        // Include subscriptions that will renew between now and end of month
        return (
          sub.status === 'active' &&
          !sub.cancel_at_period_end &&
          periodEnd > now &&
          periodEnd <= currentMonthEnd
        );
      })
      .reduce((sum, sub) => sum + sub.amount / 100, 0);

    const projectedRevenue = mtdRevenue + upcomingSubscriptionRevenue;

    const currentMonthExpenses = expenses?.amount || 0;

    // Calculate YTD expenses
    const ytdExpenses = allExpenses
      .filter((exp) => exp.year === now.getFullYear() && exp.month <= now.getMonth() + 1)
      .reduce((sum, exp) => sum + Number(exp.amount), 0);

    // Calculate profit run rate (YTD profit annualized)
    const ytdProfit = ytdRevenue - ytdExpenses;
    const monthsElapsed = now.getMonth() + 1;
    const profitRunRate = monthsElapsed > 0 ? (ytdProfit / monthsElapsed) * 12 : 0;

    // Calculate runway using cash on hand / (average of last 3 months expenses + $15k - lowest revenue from last 6 months)
    const cashOnHand = expenses?.cash_on_hand || 0;

    // Get last 3 months of expenses excluding the current month (sorted by most recent)
    const last3MonthsExcludingCurrent = allExpenses
      .filter((exp) => {
        // Exclude current month
        if (exp.year === now.getFullYear() && exp.month === now.getMonth() + 1) {
          return false;
        }
        return true;
      })
      .sort((a, b) => {
        if (b.year !== a.year) return b.year - a.year;
        return b.month - a.month;
      })
      .slice(0, 3);

    // Calculate average of last 3 months (excluding current month)
    const averageMonthlyExpense = last3MonthsExcludingCurrent.length > 0
      ? last3MonthsExcludingCurrent.reduce((sum, exp) => sum + Number(exp.amount), 0) / last3MonthsExcludingCurrent.length
      : currentMonthExpenses;

    // Calculate lowest revenue from last 6 months
    const last6MonthsRevenue: number[] = [];
    for (let i = 1; i <= 6; i++) {
      const monthDate = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
      const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);

      const monthRevenue = transactions
        .filter((t) => {
          const transactionDate = new Date(t.created_at);
          return transactionDate >= monthStart && transactionDate <= monthEnd && t.amount > 0;
        })
        .reduce((sum, t) => sum + t.amount / 100, 0);

      last6MonthsRevenue.push(monthRevenue);
    }

    const lowestRevenueFrom6Months = last6MonthsRevenue.length > 0
      ? Math.min(...last6MonthsRevenue)
      : 0;

    // Add $15,000 buffer to the average, then subtract conservative revenue
    const adjustedMonthlyExpense = (averageMonthlyExpense + 15000) - lowestRevenueFrom6Months;

    const cashRunway =
      adjustedMonthlyExpense > 0 ? cashOnHand / adjustedMonthlyExpense : 0;

    return {
      mtdRevenue,
      projectedRevenue,
      previousMonthRevenue,
      growthRate,
      runRate,
      profitRunRate,
      currentMonthExpenses,
      cashRunway,
    };
  };

  const metrics = calculateMetrics();

  const fetchStripeConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('stripe_config')
        .select('last_synced_at, stripe_publishable_key')
        .maybeSingle();

      if (error) {
        console.error('Error fetching Stripe config:', error);
        return;
      }

      setStripeConfig(data);
    } catch (err) {
      console.error('Error in fetchStripeConfig:', err);
    }
  };

  const fetchTransactions = async () => {
    try {
      const { data, error } = await supabase
        .from('stripe_transactions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching transactions:', error);
        setError('Failed to load transactions');
        return;
      }

      setTransactions((data || []) as StripeTransaction[]);
    } catch (err) {
      console.error('Error in fetchTransactions:', err);
      setError('Failed to load transactions');
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const { data, error } = await supabase
        .from('stripe_subscriptions')
        .select('*')
        .eq('status', 'active')
        .order('current_period_end', { ascending: true });

      if (error) {
        console.error('Error fetching subscriptions:', error);
        return;
      }

      setSubscriptions((data || []) as StripeSubscription[]);
    } catch (err) {
      console.error('Error in fetchSubscriptions:', err);
    }
  };

  const fetchExpenses = async () => {
    try {
      const { data, error } = await supabase
        .from('financial_expenses')
        .select('*')
        .eq('month', currentMonth)
        .eq('year', currentYear)
        .maybeSingle();

      if (error) {
        console.error('Error fetching expenses:', error);
        return;
      }

      setExpenses(data as unknown as ExpenseData | null);
    } catch (err) {
      console.error('Error in fetchExpenses:', err);
    }
  };

  const fetchAllExpenses = async () => {
    try {
      const { data, error } = await supabase
        .from('financial_expenses')
        .select('*')
        .order('year', { ascending: false })
        .order('month', { ascending: false });

      if (error) {
        console.error('Error fetching all expenses:', error);
        return;
      }

      setAllExpenses((data || []) as ExpenseData[]);
    } catch (err) {
      console.error('Error in fetchAllExpenses:', err);
    }
  };

  const refreshTransactions = async () => {
    setIsLoading(true);
    setError(null);
    await Promise.all([
      fetchTransactions(),
      fetchSubscriptions(),
      fetchExpenses(),
      fetchAllExpenses(),
      fetchStripeConfig(),
    ]);
    setIsLoading(false);
  };

  const syncStripeData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        throw new Error('Not authenticated');
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/stripe-sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to sync Stripe data');
      }

      await response.json();

      await refreshTransactions();
    } catch (err: any) {
      console.error('Error syncing Stripe data:', err);
      setError(err.message || 'Failed to sync Stripe data');
    } finally {
      setIsLoading(false);
    }
  };

  const updateExpenses = async (amount: number, notes: string, cashOnHand?: number) => {
    setIsLoading(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('Not authenticated');
      }

      const updateData: any = {
        month: currentMonth,
        year: currentYear,
        amount,
        notes,
        updated_by: user.id,
        updated_at: new Date().toISOString(),
      };

      // Only update cash_on_hand if provided
      if (cashOnHand !== undefined) {
        updateData.cash_on_hand = cashOnHand;
      }

      const { data, error } = await supabase
        .from('financial_expenses')
        .upsert(
          updateData,
          {
            onConflict: 'month,year',
          }
        )
        .select()
        .single();

      if (error) {
        throw error;
      }

      setExpenses(data as unknown as ExpenseData);
    } catch (err: any) {
      console.error('Error updating expenses:', err);
      setError(err.message || 'Failed to update expenses');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refreshTransactions();
  }, []);

  const value = {
    transactions,
    expenses,
    metrics,
    stripeConfig,
    isLoading,
    error,
    refreshTransactions,
    syncStripeData,
    updateExpenses,
  };

  return (
    <StripeFinancialsContext.Provider value={value}>
      {children}
    </StripeFinancialsContext.Provider>
  );
}

export function useStripeFinancials(): StripeFinancialsContextType {
  const context = useContext(StripeFinancialsContext);

  if (context === null) {
    throw new Error('useStripeFinancials must be used within a StripeFinancialsProvider');
  }

  return context;
}
